import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import * as v from "valibot";

// Parses a string value into a boolean. Typically useful for URLSearchParams.
const stringBoolean = (defaultValue = false) =>
  v.optional(
    v.pipe(
      v.string(),
      v.rawTransform(({ dataset: { value }, addIssue, NEVER }) => {
        switch (value.toLowerCase()) {
          case "yes":
            return true;
          case "no":
            return false;
          default:
            addIssue({ message: "unexpected boolean value" });
            return NEVER;
        }
      }),
    ),
    defaultValue ? "yes" : "no",
  );

const schema = v.object({
  needsApproval: stringBoolean(false),
});

export type CompetitionApprovalsFilters = v.InferOutput<typeof schema>;

const defaultFilters: CompetitionApprovalsFilters = {
  needsApproval: true,
};

const parseFilters = (params: unknown) => {
  const parsed = v.safeParse(schema, params);
  return parsed.success ? parsed.output : undefined;
};

export const useCompetitionApprovalsFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () =>
      parseFilters(Object.fromEntries(searchParams.entries())) ??
      defaultFilters,
    [searchParams],
  );

  const setFilters = useCallback(
    (filters: Partial<CompetitionApprovalsFilters>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(filters)) {
            next.set(key, value ? "yes" : "no");
          }
          return next;
        },
        {
          preventScrollReset: true,
        },
      );
    },
    [setSearchParams],
  );

  return [filters, setFilters] as const;
};

export const useSetSearchCompetitionApprovalsFilters = () => {
  const [_filters, setFilters] = useCompetitionApprovalsFilters();
  return setFilters;
};

export const getCompetitionApprovalsFiltersParams = (request: Request) =>
  parseFilters(
    Object.fromEntries(new URL(request.url).searchParams.entries()),
  ) ?? defaultFilters;
